/**
 * Client-side logger utility
 * Provides structured logging with different levels
 * In production, only errors and warnings are logged
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LoggerConfig {
  enabled: boolean
  minLevel: LogLevel
}

interface LogEntry {
  timestamp: string
  level: LogLevel
  message: string
  args: any[]
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

class Logger {
  private config: LoggerConfig
  private logs: LogEntry[] = []
  private readonly MAX_LOGS = 1000

  constructor() {
    const isProduction = import.meta.env.PROD
    // Default: 'warn' in prod, 'info' in dev. Debug logs are off by default in dev too —
    // they're a firehose (especially executeTransaction). To turn them on for a session
    // run `localStorage.setItem('logLevel', 'debug')` in the browser console and reload.
    let minLevel: LogLevel = isProduction ? 'warn' : 'info'
    if (typeof window !== 'undefined') {
      const override = window.localStorage?.getItem('logLevel')
      if (override === 'debug' || override === 'info' || override === 'warn' || override === 'error') {
        minLevel = override
      }
    }
    this.config = {
      enabled: true,
      minLevel,
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enabled && LOG_LEVELS[level] >= LOG_LEVELS[this.config.minLevel]
  }

  private formatMessage(level: LogLevel, message: string, ...args: any[]): void {
    if (!this.shouldLog(level)) {
      return
    }

    const timestamp = new Date().toISOString()
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`

    // Store in log history
    this.logs.push({
      timestamp,
      level,
      message,
      args,
    })

    // Limit log history size
    if (this.logs.length > this.MAX_LOGS) {
      this.logs.shift()
    }

    /* eslint-disable no-console */
    switch (level) {
      case 'debug':
        console.log(prefix, message, ...args)
        break
      case 'info':
        console.info(prefix, message, ...args)
        break
      case 'warn':
        console.warn(prefix, message, ...args)
        break
      case 'error':
        console.error(prefix, message, ...args)
        break
    }
    /* eslint-enable no-console */
  }

  debug(message: string, ...args: any[]): void {
    this.formatMessage('debug', message, ...args)
  }

  info(message: string, ...args: any[]): void {
    this.formatMessage('info', message, ...args)
  }

  warn(message: string, ...args: any[]): void {
    this.formatMessage('warn', message, ...args)
  }

  error(message: string, ...args: any[]): void {
    this.formatMessage('error', message, ...args)
  }

  /**
   * Log an error object with stack trace
   */
  logError(message: string, error: Error | unknown): void {
    if (error instanceof Error) {
      this.error(message, {
        message: error.message,
        stack: error.stack,
        name: error.name,
      })
    } else {
      this.error(message, error)
    }
  }

  /**
   * Get all stored logs as formatted text
   */
  getAllLogs(): string {
    if (this.logs.length === 0) {
      return 'No logs available'
    }

    return this.logs
      .map((entry) => {
        const argsStr =
          entry.args.length > 0
            ? ' ' +
              entry.args
                .map((arg) => {
                  try {
                    return typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
                  } catch {
                    return String(arg)
                  }
                })
                .join(' ')
            : ''

        return `[${entry.timestamp}] [${entry.level.toUpperCase()}] ${entry.message}${argsStr}`
      })
      .join('\n')
  }

  /**
   * Clear all stored logs
   */
  clearLogs(): void {
    this.logs = []
  }

  /**
   * Get number of stored logs
   */
  getLogCount(): number {
    return this.logs.length
  }
}

export const logger = new Logger()
